/**
 * The `quote_preview` view contract (design D18): a replica of a quote the
 * assistant shows before the user confirms it. The backend
 * (`show_quote_preview`) sends codes and raw values only; every user-visible
 * word is rendered by `QuotePreviewView` in the user's language. Nothing is
 * created for it; the real quote is created after the user confirmed.
 */
import type { AgentViewSpec } from "./registry.js";

export type QuotePreviewNote =
  | { code: "free" }
  | { code: "noPrice" }
  | { code: "noLabourRate" }
  | { code: "labourNotIncluded" }
  | { code: "productNotFound" }
  | { code: "noListPrice" }
  | { code: "otherCurrency"; currency: string }
  | { code: "productChosenLater"; preselected: string | null }
  | { code: "buildingBlock"; name: string }
  | { code: "typeRecommendation" };

const ITEM_KINDS = [
  "position",
  "product",
  "product_placeholder",
  "template",
  "template_type",
] as const;

export type QuotePreviewItem = {
  number: string;
  title: string;
  description: string | null;
  kind: (typeof ITEM_KINDS)[number];
  quantity: number | null;
  /** Raw unit code of the platform (`piece`, `m2`, `lump_sum`, ...). */
  quantityUnit: string | null;
  unitPrice: number | null;
  total: number | null;
  /** Shown, but not in the sums. */
  optional: boolean;
  /** Non-default option of a single-choice decision: shown, not in the sums. */
  alternative: boolean;
  alternativeGroup: string | null;
  imageUrl: string | null;
  articleNumber: string | null;
  supplierId: string | null;
  note: QuotePreviewNote | null;
};

export type QuotePreviewSection = {
  number: string;
  /** `null` = the section holding the loose top-level items. */
  title: string | null;
  items: QuotePreviewItem[];
  subtotal: number | null;
};

export type QuotePreviewAnswer = {
  key: string;
  value: string | number | boolean | null;
};

export type QuotePreviewViewSpec = {
  component: "quote_preview";
  title: string;
  customer: string | null;
  project: string | null;
  currency: string;
  /** A fraction, e.g. `0.19`. */
  vatRate: number;
  vatAssumed: boolean;
  sections: QuotePreviewSection[];
  totals: { net: number | null; vat: number | null; gross: number | null };
  missingPrices: number;
  notEvaluated: boolean;
  contentFromAnswers: boolean;
  answers: QuotePreviewAnswer[];
  templateId: string;
  /** The history note for the model; never rendered. */
  memory: string;
};

/** Items shown per section in the chat card before the rest is expanded. */
export const QUOTE_PREVIEW_CARD_ITEMS_PER_SECTION = 5;

// --- type guard (no `as`, no `any`, no `!`) -------------------------------

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isNullableString(value: unknown): value is string | null {
  return value === null || typeof value === "string";
}

function isNullableNumber(value: unknown): value is number | null {
  return value === null || (typeof value === "number" && !isNaN(value));
}

function isNote(value: unknown): value is QuotePreviewNote {
  if (!isRecord(value)) return false;
  switch (value.code) {
    case "free":
    case "noPrice":
    case "noLabourRate":
    case "labourNotIncluded":
    case "productNotFound":
    case "noListPrice":
    case "typeRecommendation":
      return true;
    case "otherCurrency":
      return typeof value.currency === "string";
    case "productChosenLater":
      return isNullableString(value.preselected);
    case "buildingBlock":
      return typeof value.name === "string";
    default:
      return false;
  }
}

function isItem(value: unknown): value is QuotePreviewItem {
  return (
    isRecord(value) &&
    typeof value.number === "string" &&
    typeof value.title === "string" &&
    isNullableString(value.description) &&
    ITEM_KINDS.some(kind => kind === value.kind) &&
    isNullableNumber(value.quantity) &&
    isNullableString(value.quantityUnit) &&
    isNullableNumber(value.unitPrice) &&
    isNullableNumber(value.total) &&
    typeof value.optional === "boolean" &&
    typeof value.alternative === "boolean" &&
    isNullableString(value.alternativeGroup) &&
    isNullableString(value.imageUrl) &&
    isNullableString(value.articleNumber) &&
    isNullableString(value.supplierId) &&
    (value.note === null || isNote(value.note))
  );
}

function isSection(value: unknown): value is QuotePreviewSection {
  return (
    isRecord(value) &&
    typeof value.number === "string" &&
    isNullableString(value.title) &&
    Array.isArray(value.items) &&
    value.items.every(isItem) &&
    isNullableNumber(value.subtotal)
  );
}

function isAnswer(value: unknown): value is QuotePreviewAnswer {
  return (
    isRecord(value) &&
    typeof value.key === "string" &&
    (value.value === null ||
      typeof value.value === "string" ||
      typeof value.value === "number" ||
      typeof value.value === "boolean")
  );
}

function isTotals(value: unknown): value is QuotePreviewViewSpec["totals"] {
  return (
    isRecord(value) &&
    isNullableNumber(value.net) &&
    isNullableNumber(value.vat) &&
    isNullableNumber(value.gross)
  );
}

export function isQuotePreviewView(
  view: AgentViewSpec
): view is QuotePreviewViewSpec {
  return (
    view.component === "quote_preview" &&
    typeof view.title === "string" &&
    isNullableString(view.customer) &&
    isNullableString(view.project) &&
    typeof view.currency === "string" &&
    typeof view.vatRate === "number" &&
    typeof view.vatAssumed === "boolean" &&
    Array.isArray(view.sections) &&
    view.sections.every(isSection) &&
    isTotals(view.totals) &&
    typeof view.missingPrices === "number" &&
    typeof view.notEvaluated === "boolean" &&
    typeof view.contentFromAnswers === "boolean" &&
    Array.isArray(view.answers) &&
    view.answers.every(isAnswer) &&
    typeof view.templateId === "string" &&
    typeof view.memory === "string"
  );
}

// --- amounts ----------------------------------------------------------------

/**
 * "–" for a missing amount, otherwise the app's price formatting (the
 * `getFormattedPrice` of `useFormatting`, which formats in euros: the
 * preview's currency is always EUR).
 */
export function formatQuoteAmount(
  amount: number | null,
  getFormattedPrice: (value: number | null) => string | undefined
): string {
  if (amount === null) return "–";
  return getFormattedPrice(amount) ?? "–";
}
